import { FC, MouseEvent, useCallback, useMemo } from 'react'
import { Project } from '@brg/api'
import { SortableKnob } from 'react-easy-sort'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { LayoutConst } from 'core'
import DragIcon from 'shared/icons/DragIcon'
import CheckSmallIcon from 'shared/icons/CheckSmallIcon'
import { LMSModuleCover } from 'shared/components/LMSModuleCover/LMSModuleCover'
import { TeamHolder } from 'shared/components/TeamCard/TeamHolder'
import { SelectRing } from 'shared/components/projectCard/projectCardNew'
import { ProjectCardTitle } from 'shared/components/projectCard/styles'
import { RingPositions } from 'shared/constants/ringPositionType'
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam'
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes'
import { isOrganizationsEntityType } from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter'
import {
  getProjectTitleFirstLetter,
  getTeamTabOrderCardUrl,
  getTeamTabProjectCoverUrl,
} from './getTeamTabProjectCoverUrl'
import {
  isTeamTabProjectWideAspect,
  isTeamTabProjectWideCover,
} from './teamTabProjectCoverLayout'

const portraitCardSize = (breakpoint: 'l' | 'm' | 's') => css`
  width: ${LayoutConst.projectCard[breakpoint].w}px;
  height: ${LayoutConst.projectCard[breakpoint].h}px;
`

const wideCardSize = (breakpoint: 'l' | 'm' | 's', useWideAspect: boolean) => {
  const heightKey = useWideAspect ? 'h1_9' : 'h'
  return css`
    width: 100%;
    max-width: 100%;
    height: ${LayoutConst.projectCard[breakpoint][heightKey]}px;
  `
}

const CardRoot = styled.div<{ $wide: boolean; $wideAspect: boolean }>`
  position: relative;
  ${({ $wide, $wideAspect }) => ($wide ? wideCardSize('l', $wideAspect) : portraitCardSize('l'))}
  flex-shrink: 0;
  overflow: hidden;
  background-color: #f2f2f2;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(24, 26, 28, 0);
    transition: background 0.2s ease;
    pointer-events: none;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(24, 26, 28, 0.12);
  }

  &:hover::after {
    background: rgba(24, 26, 28, 0.12);
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    ${({ $wide, $wideAspect }) => ($wide ? wideCardSize('m', $wideAspect) : portraitCardSize('m'))}
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    ${({ $wide, $wideAspect }) => ($wide ? wideCardSize('s', $wideAspect) : portraitCardSize('s'))}
  }
`

const CoverImage = styled.img<{ $wide: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  user-select: none;
  object-fit: cover;
  object-position: center center;

  ${({ $wide }) => $wide && css`
    object-position: center top;
  `}
`

const CoverTitlePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.imagePlaceholder?.bg ?? '#f2f2f2'};
  color: ${({ theme }) => theme.imagePlaceholder?.icon ?? '#909090'};
  font-family: Inter, sans-serif;
  font-size: 120px;
  font-weight: 600;
  line-height: 1;
`

const CoverFallbackWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`

const CardControls = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const DragHandleButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  background: rgba(24, 26, 28, 0.5);
  border-radius: 4px;
  width: 32px;
  height: 32px;
  min-width: 32px;

  svg {
    pointer-events: none;
  }
`

type TeamTabProjectOrderCardProps = {
  project: Project
  entityType?: string
  selected: boolean
  showDragHandle?: boolean
  onToggleSelect: (projectId: string) => void
}

export const TeamTabProjectOrderCard: FC<TeamTabProjectOrderCardProps> = ({
  project,
  entityType,
  selected,
  showDragHandle = true,
  onToggleSelect,
}) => {
  const { i18n } = useTranslation()
  const isWide = useMemo(() => isTeamTabProjectWideCover(project), [project])
  const isWideAspect = useMemo(() => isTeamTabProjectWideAspect(project), [project])
  const coverUrl = useMemo(
    () => getTeamTabProjectCoverUrl(project, i18n.language),
    [project, i18n.language],
  )
  const titleLetter = useMemo(() => getProjectTitleFirstLetter(project), [project])
  const team = project as ComputedTeam
  const isOrganizationCard = isOrganizationsEntityType(entityType)
  const isLmsOrganization = isOrganizationCard && team?.moderationType === ModerationType.LMS
  const organizationCoverTitle = useMemo(() => {
    const name = typeof team?.name === 'string' ? team.name.trim() : project?.title
    if (!name) {
      return undefined
    }

    return <ProjectCardTitle>{name}</ProjectCardTitle>
  }, [team?.name, project?.title])
  const lmsDiscipline = useMemo(
    () => (project as { additionalInfo?: { lmsmodule_discipline?: string } })?.additionalInfo?.lmsmodule_discipline || '',
    [project],
  )

  const cardUrl = useMemo(
    () => getTeamTabOrderCardUrl(project, entityType),
    [project, entityType],
  )

  const handleToggle = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onToggleSelect(project.projectId)
  }

  const handleDragHandleClick = (event: MouseEvent) => {
    event.stopPropagation()
  }

  const handleCardClick = useCallback(() => {
    if (!cardUrl) return
    window.open(cardUrl, '_blank', 'noopener,noreferrer')
  }, [cardUrl])

  return (
    <CardRoot $wide={isWide} $wideAspect={isWideAspect} onClick={handleCardClick}>
      {coverUrl ? (
        <CoverImage $wide={isWide} src={coverUrl} alt="" draggable={false} />
      ) : isLmsOrganization ? (
        <CoverFallbackWrapper>
          <LMSModuleCover
            project={project}
            title={organizationCoverTitle}
            discipline={lmsDiscipline}
          />
        </CoverFallbackWrapper>
      ) : isOrganizationCard ? (
        <CoverFallbackWrapper>
          <TeamHolder team={team} />
        </CoverFallbackWrapper>
      ) : (
        <CoverTitlePlaceholder aria-hidden>{titleLetter}</CoverTitlePlaceholder>
      )}
      <CardControls>
        <SelectRing
          className="selectRing"
          active={selected}
          ringPosition={RingPositions.left}
          onClick={handleToggle}
        >
          {selected && (
            <div>
              <CheckSmallIcon />
            </div>
          )}
        </SelectRing>
        {showDragHandle ? (
          <SortableKnob>
            <DragHandleButton onClick={handleDragHandleClick}>
              <DragIcon style={{ color: '#fefefe' }} fill="#fefefe" />
            </DragHandleButton>
          </SortableKnob>
        ) : (
          <span aria-hidden />
        )}
      </CardControls>
    </CardRoot>
  )
}
import { FC, MouseEvent, useCallback, useMemo } from 'react'
import { Project } from '@brg/api'
import { SortableKnob } from 'react-easy-sort'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { LayoutConst } from 'core'
import DragIcon from 'shared/icons/DragIcon'
import CheckSmallIcon from 'shared/icons/CheckSmallIcon'
import { LMSModuleCover } from 'shared/components/LMSModuleCover/LMSModuleCover'
import { TeamHolder } from 'shared/components/TeamCard/TeamHolder'
import { SelectRing } from 'shared/components/projectCard/projectCardNew'
import { ProjectCardTitle } from 'shared/components/projectCard/styles'
import { RingPositions } from 'shared/constants/ringPositionType'
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam'
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes'
import { isOrganizationsEntityType } from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter'
import {
  getProjectTitleFirstLetter,
  getTeamTabOrderCardUrl,
  getTeamTabProjectCoverUrl,
} from './getTeamTabProjectCoverUrl'
import {
  isTeamTabProjectWideAspect,
  isTeamTabProjectWideCover,
} from './teamTabProjectCoverLayout'

const portraitCardSize = (breakpoint: 'l' | 'm' | 's') => css`
  width: ${LayoutConst.projectCard[breakpoint].w}px;
  height: ${LayoutConst.projectCard[breakpoint].h}px;
`

const wideCardSize = (breakpoint: 'l' | 'm' | 's', useWideAspect: boolean) => {
  const heightKey = useWideAspect ? 'h1_9' : 'h'
  return css`
    width: 100%;
    max-width: 100%;
    height: ${LayoutConst.projectCard[breakpoint][heightKey]}px;
  `
}

const CardRoot = styled.div<{ $wide: boolean; $wideAspect: boolean }>`
  position: relative;
  ${({ $wide, $wideAspect }) => ($wide ? wideCardSize('l', $wideAspect) : portraitCardSize('l'))}
  flex-shrink: 0;
  overflow: hidden;
  background-color: #f2f2f2;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(24, 26, 28, 0);
    transition: background 0.2s ease;
    pointer-events: none;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(24, 26, 28, 0.12);
  }

  &:hover::after {
    background: rgba(24, 26, 28, 0.12);
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    ${({ $wide, $wideAspect }) => ($wide ? wideCardSize('m', $wideAspect) : portraitCardSize('m'))}
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    ${({ $wide, $wideAspect }) => ($wide ? wideCardSize('s', $wideAspect) : portraitCardSize('s'))}
  }
`

const CoverImage = styled.img<{ $wide: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  user-select: none;
  object-fit: cover;
  object-position: center center;

  ${({ $wide }) => $wide && css`
    object-position: center top;
  `}
`

const CoverTitlePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.imagePlaceholder?.bg ?? '#f2f2f2'};
  color: ${({ theme }) => theme.imagePlaceholder?.icon ?? '#909090'};
  font-family: Inter, sans-serif;
  font-size: 120px;
  font-weight: 600;
  line-height: 1;
`

const CoverFallbackWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`

const CardControls = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const DragHandleButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  background: rgba(24, 26, 28, 0.5);
  border-radius: 4px;
  width: 32px;
  height: 32px;
  min-width: 32px;

  svg {
    pointer-events: none;
  }
`

type TeamTabProjectOrderCardProps = {
  project: Project
  entityType?: string
  selected: boolean
  showDragHandle?: boolean
  onToggleSelect: (projectId: string) => void
}

export const TeamTabProjectOrderCard: FC<TeamTabProjectOrderCardProps> = ({
  project,
  entityType,
  selected,
  showDragHandle = true,
  onToggleSelect,
}) => {
  const { i18n } = useTranslation()
  const isWide = useMemo(() => isTeamTabProjectWideCover(project), [project])
  const isWideAspect = useMemo(() => isTeamTabProjectWideAspect(project), [project])
  const coverUrl = useMemo(
    () => getTeamTabProjectCoverUrl(project, i18n.language),
    [project, i18n.language],
  )
  const titleLetter = useMemo(() => getProjectTitleFirstLetter(project), [project])
  const team = project as ComputedTeam
  const isOrganizationCard = isOrganizationsEntityType(entityType)
  const isLmsOrganization = isOrganizationCard && team?.moderationType === ModerationType.LMS
  const organizationCoverTitle = useMemo(() => {
    const name = typeof team?.name === 'string' ? team.name.trim() : project?.title
    if (!name) {
      return undefined
    }

    return <ProjectCardTitle>{name}</ProjectCardTitle>
  }, [team?.name, project?.title])
  const lmsDiscipline = useMemo(
    () => (project as { additionalInfo?: { lmsmodule_discipline?: string } })?.additionalInfo?.lmsmodule_discipline || '',
    [project],
  )

  const cardUrl = useMemo(
    () => getTeamTabOrderCardUrl(project, entityType),
    [project, entityType],
  )

  const handleToggle = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onToggleSelect(project.projectId)
  }

  const handleDragHandleClick = (event: MouseEvent) => {
    event.stopPropagation()
  }

  const handleCardClick = useCallback(() => {
    if (!cardUrl) return
    window.open(cardUrl, '_blank', 'noopener,noreferrer')
  }, [cardUrl])

  return (
    <CardRoot $wide={isWide} $wideAspect={isWideAspect} onClick={handleCardClick}>
      {coverUrl ? (
        <CoverImage $wide={isWide} src={coverUrl} alt="" draggable={false} />
      ) : isLmsOrganization ? (
        <CoverFallbackWrapper>
          <LMSModuleCover
            project={project}
            title={organizationCoverTitle}
            discipline={lmsDiscipline}
          />
        </CoverFallbackWrapper>
      ) : isOrganizationCard ? (
        <CoverFallbackWrapper>
          <TeamHolder team={team} />
        </CoverFallbackWrapper>
      ) : (
        <CoverTitlePlaceholder aria-hidden>{titleLetter}</CoverTitlePlaceholder>
      )}
      <CardControls>
        <SelectRing
          className="selectRing"
          active={selected}
          ringPosition={RingPositions.left}
          onClick={handleToggle}
        >
          {selected && (
            <div>
              <CheckSmallIcon />
            </div>
          )}
        </SelectRing>
        {showDragHandle ? (
          <SortableKnob>
            <DragHandleButton onClick={handleDragHandleClick}>
              <DragIcon style={{ color: '#fefefe' }} fill="#fefefe" />
            </DragHandleButton>
          </SortableKnob>
        ) : (
          <span aria-hidden />
        )}
      </CardControls>
    </CardRoot>
  )
}
